import React, { useState, useEffect } from 'react';
import {
    Layout,
    Typography,
    Button,
    Card,
    Spin,
    Modal,
    Select,
    Collapse,
    message,
    Drawer,
    Tooltip,
    Row,
    Col, 
    Divider
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from './config';
import { Preferences } from '@capacitor/preferences';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

// Un numero e la sua etichetta. Il riquadro passa in evidenza quando c'e'
// qualcosa che aspetta il trainer, cosi' si vede a colpo d'occhio.
const Riquadro = ({ numero, etichetta, inEvidenza = false }) => (
    <div style={{
        background: inEvidenza ? 'var(--primario)' : 'var(--superficie)',
        border: `1px solid ${inEvidenza ? 'var(--primario)' : 'var(--bordo)'}`,
        borderRadius: 8,
        padding: '14px 10px',
        height: '100%',
        textAlign: 'center',
    }}>
        <div style={{
            fontSize: 30,
            lineHeight: 1,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            color: inEvidenza ? '#FFFFFF' : 'var(--inchiostro)',
        }}>
            {numero === null || numero === undefined ? '–' : numero}
        </div>
        <div style={{
            fontSize: 11,
            marginTop: 6,
            lineHeight: 1.25,
            color: inEvidenza ? 'rgba(255,255,255,0.85)' : 'var(--testo-tenue)',
        }}>
            {etichetta}
        </div>
    </div>
);

const TrainerDashboard = () => {
    const { user } = useUser();
    const [requests, setRequests] = useState([]);
    const [completedRequests, setCompletedRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [workoutPlans, setWorkoutPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState('');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
    const [requestToDelete, setRequestToDelete] = useState(null);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [clientWorkoutHistory, setClientWorkoutHistory] = useState([]);
    const [selectedPlanPreview, setSelectedPlanPreview] = useState(null);
    // Numeri del riepilogo: la dashboard era un pulsante e una riga di testo,
    // senza dire quanto lavoro c'e' in attesa.
    const [riepilogo, setRiepilogo] = useState({ assegnate: null, schede: null });

    const navigate = useNavigate();


    useEffect(() => {
        const checkAuthAndFetchRequests = async () => {
            const token = await Preferences.get({ key: 'token' });
            const user = await Preferences.get({ key: 'user' });
            if (!token || !user) {
                // Se non c'è token o utente, reindirizza al login
                message.error('Autenticazione richiesta. Effettua di nuovo il login.');
                navigate('/login');
            } else {
                // Se l'utente è autenticato, carica le richieste
                fetchRequests(token);
            }
        };
    
        checkAuthAndFetchRequests(); // Chiama la funzione definita sopra
    }, [user, navigate]); // Aggiungi 'user' come dipendenza
    

    useEffect(() => {
        let annullato = false;

        const caricaRiepilogo = async () => {
            try {
                const [assegnate, schede] = await Promise.all([
                    axios.get(`${apiUrl}/api/trainer/completedRequests`),
                    axios.get(`${apiUrl}/api/trainer/trainer/schede`),
                ]);
                if (annullato) return;
                setRiepilogo({
                    assegnate: Array.isArray(assegnate.data) ? assegnate.data.length : 0,
                    schede: Array.isArray(schede.data) ? schede.data.length : 0,
                });
            } catch (errore) {
                // Il riepilogo e' un di piu': se non arriva, i riquadri
                // restano senza numero e il resto della pagina funziona.
            }
        };

        caricaRiepilogo();
        return () => { annullato = true; };
    }, []);

    const fetchRequests = async () => {
        const token = (await Preferences.get({ key: 'token' })).value;
        if (!token) {
            message.error('Token mancante. Effettua di nuovo il login.');
            setLoading(false);
            return;
        }
        try {
            const response = await axios.get(`${apiUrl}/api/workoutRequests/requests`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setRequests(response.data);
        } catch (error) {
            message.error('Errore durante il recupero delle richieste.');
        } finally {
            setLoading(false);
        }
    };

    const groupExercisesByDayAndMuscle = (exercises) => {
        return exercises.reduce((acc, exercise) => {
            const { day, muscleGroup } = exercise;
            if (!acc[day]) {
                acc[day] = {};
            }
            if (!acc[day][muscleGroup]) {
                acc[day][muscleGroup] = [];
            }
            acc[day][muscleGroup].push(exercise);
            return acc;
        }, {});
    };
    

    const fetchClientWorkoutHistory = async (clientId) => {
        try {
            const token = (await Preferences.get({ key: 'token' })).value;
            const response = await axios.get(`${apiUrl}/api/workoutHistory/${clientId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setClientWorkoutHistory(response.data);
            setIsHistoryModalOpen(true);
        } catch (error) {
            message.error("Errore durante il recupero dello storico delle schede.");
        }
    };

    const fetchWorkoutPlans = async () => {
        try {
            const token = (await Preferences.get({ key: 'token' })).value;
            const response = await axios.get(`${apiUrl}/api/trainer/schede`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { trainerId: user.trainerId },
            });
    
            // Raggruppa i piani di allenamento per cartella principale e sottocartelle
            const groupedPlans = response.data.reduce((acc, plan) => {
                plan.folders.forEach((folder) => {
                    const [mainFolder, subFolder] = folder.split('/');
                    
                    if (!acc[mainFolder]) {
                        acc[mainFolder] = {};
                    }
                    if (!acc[mainFolder][subFolder]) {
                        acc[mainFolder][subFolder] = [];
                    }
                    acc[mainFolder][subFolder].push(plan);
                });
                return acc;
            }, {});
    
            // Filtra solo le cartelle popolate
            const populatedFolders = Object.keys(groupedPlans).filter(folder => 
                Object.keys(groupedPlans[folder]).some(subFolder => groupedPlans[folder][subFolder].length > 0)
            );
    
            setWorkoutPlans(populatedFolders.map(folder => ({
                folderName: folder,
                subFolders: groupedPlans[folder]
            })));
        } catch (error) {
            message.error('Errore durante il recupero dei piani.');
        }
    };
    
    

    const handleSendPlan = async () => {
        if (!selectedPlan) {
            message.error('Seleziona un piano di allenamento prima di inviarlo.');
            return;
        }
    
        try {
            const token = (await Preferences.get({ key: 'token' })).value;
            
            // Get the current timestamp
            const assignedAt = new Date().toISOString();
    
            await axios.post(`${apiUrl}/api/sendWorkoutPlan`, {
                requestId: selectedRequest._id,
                planId: selectedPlan,
                assignedAt: assignedAt  // Add the assignedAt field with current timestamp
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
    
            setCompletedRequests([...completedRequests, selectedRequest]);
            setRequests(requests.filter((request) => request._id !== selectedRequest._id));
            setOpenModal(false);
            message.success('Scheda inviata con successo!');
        } catch (error) {
            message.error('Errore durante l\'invio della scheda.');
        }
    };
    

    const toggleProfileDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    const handleOpenModal = (request) => {
        setSelectedRequest(request);
        fetchWorkoutPlans();
        setOpenModal(true);
    };

    const handleDeleteRequest = (requestId) => {
        setRequestToDelete(requestId);
        setConfirmDeleteVisible(true);
    };

    const confirmDeleteRequest = async () => {
        const token = (await Preferences.get({ key: 'token' })).value;
        try {
            await axios.delete(`${apiUrl}/api/workoutRequests/${requestToDelete}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setRequests(requests.filter((request) => request._id !== requestToDelete));
            message.success('Richiesta cancellata con successo!');
        } catch (error) {
            message.error('Errore durante la cancellazione della richiesta.');
        } finally {
            setConfirmDeleteVisible(false);
            setRequestToDelete(null);
        }
    };

    const goToProfileEdit = () => {
        navigate('/profile');
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Content style={{ padding: '10px', maxWidth: '1000px', margin: '0 auto' }}>
            <Row justify="center" style={{ marginBottom: '20px' }}>
                <Col xs={24}>
                <div className="dashboard-title">
                    DASHBOARD TRAINER
                </div>
                </Col>
                <Col xs={24} style={{ marginTop: '10px' }}>
                    <Row gutter={[10, 10]}>
                        <Col xs={8}>
                            <Riquadro
                                numero={loading ? null : requests.length}
                                etichetta={requests.length === 1 ? 'richiesta da evadere' : 'richieste da evadere'}
                                inEvidenza={!loading && requests.length > 0}
                            />
                        </Col>
                        <Col xs={8}>
                            <Riquadro numero={riepilogo.assegnate} etichetta="schede assegnate" />
                        </Col>
                        <Col xs={8}>
                            <Riquadro numero={riepilogo.schede} etichetta="schede in archivio" />
                        </Col>
                    </Row>
                </Col>
                <Col xs={24} style={{ marginTop: '12px' }}>
                    <Button type="primary" block onClick={() => navigate('/workout-plans')}>
                        Visualizza Schede di Allenamento
                    </Button>
                </Col>
            </Row>


                {/* Requests Section */}
                <Title level={4}>Richieste Attive</Title>
                {loading ? (
                    <Spin style={{ display: 'block', marginTop: '2rem' }} />
                ) : requests.length > 0 ? (
                    requests.map((request, index) => (
                        <Card key={index} style={{ marginBottom: '1rem', padding: '20px' }}>
                            <Row justify="space-between" align="middle">
                                <Col xs={24} md={12}>
                                    <div className='richiesta'>Richiesta da: <br/>{request.nome} {request.cognome}</div>
                                    <div className='frequenza'>Frequenza settimanale: {request.frequenza} volte </div>
                                    {request.note && (
                                        <Text style={{ fontSize: '16px', display: 'block', marginTop: '0.5rem' }}>
                                            <b>Note:</b> {request.note}
                                        </Text>
                                    )}
                                </Col>
                                <Col xs={24} md={24}>
    <div style={{ 
        marginTop: '1rem', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        gap: '1rem', 
        flexWrap: 'wrap',
        maxWidth: '100%'  // Limita la larghezza totale
        }}>
            <Button 
                type="primary" 
                onClick={() => handleOpenModal(request)} 
                style={{ flexBasis: '30%' }}  // Imposta una larghezza flessibile per ogni pulsante
            >
                Invia una scheda
            </Button>
            <Button
                type="default"
                onClick={() => fetchClientWorkoutHistory(request.email)}
                style={{ flexBasis: '30%' }}
            >
                Storico Schede
            </Button>
            <Tooltip title="Cancella Richiesta">
                <Button
                    icon={<DeleteOutlined style={{ color: '#ffffff' }} />} 
                    onClick={() => handleDeleteRequest(request._id)}
                    className="custom-trash-button"
                    style={{ flexBasis: '30%' }}
                />
            </Tooltip>
        </div>
    </Col>



                            </Row>
                        </Card>
                    ))
                ) : (
                    <Text>Nessuna richiesta disponibile.</Text>
                )}

                {/* Completed Requests Section */}
                {completedRequests.length > 0 && (
                    <Collapse style={{ marginTop: '2rem' }} accordion>
                        <Panel header="Storico Richieste Completate" key="1">
                            {completedRequests.map((request, index) => (
                                <Card key={index} style={{ marginBottom: '1rem', padding: '20px' }}>
                                    <Title level={5} style={{ fontSize: '18px' }}>Richiesta Completata da: {request.nome} {request.cognome}</Title>
                                    <Text style={{ fontSize: '16px' }}>Frequenza settimanale: {request.frequenza} volte</Text>
                                </Card>
                            ))}
                        </Panel>
                    </Collapse>
                )}
            </Content>

            {/* Drawer for Profile */}
            <Drawer
                title="Profilo"
                placement="right"
                onClose={toggleProfileDrawer}
                open={isDrawerOpen} // `open` instead of `visible`
                getContainer={document.body}
                width={300}
            >
                <p><b>Nome:</b> {user?.nome}</p>
                <p><b>Email:</b> {user?.email}</p>
                <Button type="primary" block onClick={goToProfileEdit}>
                    Modifica Profilo
                </Button>
            </Drawer>

            <Modal
    title="Seleziona un piano di allenamento"
    open={openModal}
    onCancel={() => setOpenModal(false)}
    getContainer={document.body}
    onOk={handleSendPlan}
    okText="Invia Scheda"
    cancelText="Annulla"
    className="custom-modal"
>
    <Collapse>
        {workoutPlans.map((folder, index) => (
            <Panel header={folder.folderName} key={index}>
                <Collapse>
                    {Object.keys(folder.subFolders).map((subFolder, subIndex) => (
                        <Panel header={subFolder} key={subIndex}>
                            <Select
                                placeholder="Seleziona un piano"
                                value={selectedPlan}
                                onChange={(planId) => {
                                    setSelectedPlan(planId);
                                    const plan = folder.subFolders[subFolder].find((p) => p._id === planId);
                                    if (plan) setSelectedPlanPreview(plan);
                                }}
                                style={{ width: '100%', marginBottom: '1rem' }}
                            >
                                {folder.subFolders[subFolder].map((plan) => (
                                    <Option key={plan._id} value={plan._id}>
                                        {plan.workoutPlanName || 'Piano senza nome'}
                                    </Option>
                                ))}
                            </Select>

                            {/* Dettagli della scheda selezionata */}
{/* Dettagli della scheda selezionata */}
{selectedPlanPreview && selectedPlanPreview._id === selectedPlan && (
    <div style={{ marginTop: '1rem', padding: '1rem', border: '1px solid var(--bordo)', borderRadius: '8px' }}>
        <Title level={5}>Dettagli del Piano: {selectedPlanPreview.workoutPlanName}</Title>
        <Collapse>
            {Object.entries(groupExercisesByDayAndMuscle(selectedPlanPreview.exercises || [])).map(
                ([day, muscleGroups], dayIndex) => {
                    let globalExerciseIndex = 0; // Inizializza il contatore globale per il giorno
                    return (
                        <Panel header={`Giorno ${day}`} key={dayIndex}>
                            {Object.entries(muscleGroups).map(([muscleGroup, exercises], muscleIndex) => (
                                <div key={muscleIndex}>
                                    <Text strong>{muscleGroup}</Text>
                                    {exercises.map((exercise, exerciseIndex) => {
                                        globalExerciseIndex++; // Incrementa il contatore globale
                                        return (
                                            <div
                                                key={exerciseIndex}
                                                style={{
                                                    margin: '10px 0',
                                                    padding: '10px',
                                                    border: '1px solid var(--bordo)',
                                                    borderRadius: '5px',
                                                }}
                                            >
                                                <p><strong>Esercizio {globalExerciseIndex}:</strong> {exercise.name}</p>
                                                <p><strong>Serie:</strong> {exercise.series}</p>
                                                <p><strong>Ripetizioni:</strong> {exercise.reps}</p>
                                                {exercise.rest && <p><strong>Riposo:</strong> {exercise.rest} sec</p>}
                                                {exercise.note && <p><strong>Note:</strong> {exercise.note}</p>}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </Panel>
                    );
                }
            )}
        </Collapse>
    </div>
)}


                        </Panel>
                    ))}
                </Collapse>
            </Panel>
        ))}
    </Collapse>
</Modal>




            {/* Confirm Delete Modal */}
            <Modal
                title="Conferma Cancellazione"
                open= {confirmDeleteVisible}
                getContainer={document.body}
                onOk={confirmDeleteRequest}
                onCancel={() => setConfirmDeleteVisible(false)}
                okText="Conferma"
                cancelText="Annulla"
                className="custom-modal"
            >
                <p>Sei sicuro di voler cancellare questa richiesta?</p>
            </Modal>

<Modal
    title="Storico Schede Assegnate"
    open={isHistoryModalOpen}
    onCancel={() => setIsHistoryModalOpen(false)}
    getContainer={document.body}
    bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    footer={null}
    width={600}
>
    {clientWorkoutHistory.length > 0 ? (
        <Collapse accordion>
            {clientWorkoutHistory.map((item, index) => (
                <Panel
                    key={index}
                    header={
                        <div>
                            <strong>{item.workoutPlanId?.workoutPlanName || 'Nome non disponibile'}</strong>
                            <span style={{ fontSize: '14px', color: '#888', marginLeft: '10px' }}>
                                Assegnato il: {new Date(item.assignedAt).toLocaleDateString()}
                            </span>
                        </div>
                    }
                >
                    {/* Raggruppa esercizi per giorno */}
                    {Object.entries(groupExercisesByDayAndMuscle(item.workoutPlanId?.exercises)).map(([day, muscles]) => (
                        <div key={day} style={{ marginBottom: '20px' }}>
                            <Text strong style={{ fontSize: '16px' }}>Giorno {day}</Text>
                            <Collapse>
                                {Object.entries(muscles).map(([muscleGroup, exercises], muscleIndex) => (
                                    <Panel
                                        header={<Text strong>{muscleGroup}</Text>}
                                        key={muscleIndex}
                                    >
                                        {exercises.map((exercise, exerciseIndex) => (
                                            <div key={exerciseIndex} style={{ marginBottom: '10px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    {/* Immagine piccola */}
                                                    <img
                                                        src={`/${exercise.image}`}
                                                        alt={exercise.name}
                                                        style={{ width: '40px', height: '40px', objectFit: 'cover', marginRight: '10px' }}
                                                    />
                                                    {/* Dettagli esercizio */}
                                                    <div>
                                                        <p><strong>Esercizio:</strong> {exercise.name}</p>
                                                        <p><strong>Serie:</strong> {exercise.series}</p>
                                                        <p><strong>Ripetizioni:</strong> {exercise.reps}</p>
                                                        {exercise.rest && <p><strong>Riposo:</strong> {exercise.rest} sec</p>}
                                                        {exercise.note && <p><strong>Note:</strong> {exercise.note}</p>}
                                                    </div>
                                                </div>
                                                {exerciseIndex < exercises.length - 1 && <Divider />} {/* Linea tra esercizi */}
                                            </div>
                                        ))}
                                    </Panel>
                                ))}
                            </Collapse>
                        </div>
                    ))}
                </Panel>
            ))}
        </Collapse>
    ) : (
        <Text>Nessuna scheda assegnata trovata per questo cliente.</Text>
    )}
</Modal>


        </Layout>
    );
};

export default TrainerDashboard;
